import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Grid,
    Button,
    Snackbar,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Chip,
    Autocomplete,
    Paper,
    MenuList,
    ClickAwayListener,
    Popper,
    Checkbox
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
      QuestionAnswer as VerifyIcon
} from '@mui/icons-material';
import { useParams, useLocation } from 'react-router-dom';
import FileUploadComponent from '../../../components/FileUploadComponent';
import TestingLaboratoryService from '../../../services/testinglaboratoryService';
import UnfreezeApiService from '../../../services/UnfreezeApiService';
import {
    insertDANC,
    updateDANC,
    updateDANCBySourceId,
    getDANCById,
    getDANC
} from '../../../services/apiCalibration';
import {
    getScrutinyRemarks,
    createScrutinyRemark,
    getClauseNo
} from '../../../services/api';
import s3Utils from '../../../utils/s3Utils';
import { decryptText, decryptAppId } from '../../../utils/encryption';

// Adequacy Check Component with DA NC Integration on Submit
const AdequacyCheckSection = React.memo(({
    sectionId,
    title,
    applicationStage,
    orgId,
    appId,
    adequacyChecks,
    handleAdequacyCheck,
    handleAdequacyRemarks,
    handleAdequacySubmit,
    loading,
    currentUser = 'System User',
    currentUserId = 0,
    getAssessmentIdFromQuery,
    getAssessorIdFromQuery,
    refreshDANCData // Add refresh callback prop
}) => {
    // Local state for DA NC operations
    const [dancLoading, setDancLoading] = useState(false);
    const [dancError, setDancError] = useState('');
    const [dancSuccess, setDancSuccess] = useState('');
    const [existingDANCRecord, setExistingDANCRecord] = useState(null);
    const [dataLoaded, setDataLoaded] = useState(false);
    
    // State for scrutiny remarks
    const [scrutinyRemarks, setScrutinyRemarks] = useState([]);
    const [loadingRemarks, setLoadingRemarks] = useState(false);
    const [isDropdownSelection, setIsDropdownSelection] = useState(false);

    // State for checkbox dropdown
    const [selectedRemarksOptions, setSelectedRemarksOptions] = useState([]);
    const [isCheckboxDropdownOpen, setIsCheckboxDropdownOpen] = useState(false);

    // Clause number states
    const [clauses, setClauses] = useState([]);
    const [selectedClause, setSelectedClause] = useState('');
    const [loadingClauses, setLoadingClauses] = useState(false);

    // Load existing DA NC data on component mount
    useEffect(() => {
        const loadExistingDANCData = async () => {
            if (!orgId || !appId || !title || dataLoaded) return;

            try {
                setDancLoading(true);
                
                // Determine NCtype based on userTypeId
                const userTypeId = localStorage.getItem('userTypeId');
                let expectedNCType = 'Document Adequacy'; // Default
                
                if (userTypeId === '4') {
                    expectedNCType = 'Document Adequacy';
                } else if (userTypeId === '6') {
                    expectedNCType = 'Assessment';
                }
                
                console.log(`🔍 Loading DANC data for userType ${userTypeId} with NCtype: ${expectedNCType}`);
                
                // Use getDANC with filters to get records for this section
                const response = await getDANC({
                    orgid: orgId,
                    appid: appId,
                    sectionName: title,
                    subsectionName: sectionId,
                    isactive: 1
                });
                
                if (response.data.success && response.data.data && response.data.data.length > 0) {
                    console.log(`📋 Found ${response.data.data.length} total DANC records for section: ${title}`);
                    
                    // Filter by NCtype to get the record for current userType
                    const filteredRecords = response.data.data.filter(record => 
                        record.NCtype === expectedNCType
                    );
                    
                    console.log(`🎯 Filtered to ${filteredRecords.length} records with NCtype: ${expectedNCType}`);
                    
                    if (filteredRecords.length > 0) {
                        const recordData = filteredRecords[0];
                        console.log(`✅ Existing Record found with NCtype ${recordData.NCtype}:`, recordData);
                        setExistingDANCRecord(recordData);
                        
                        // Bind DA NC data to adequacy check fields
                        console.log(`🔗 Binding DANC data to adequacy check section: ${sectionId}`);
                        
                        if (recordData.InformationStatus) {
                            console.log(`📊 Setting adequacy check status: ${recordData.InformationStatus}`);
                            handleAdequacyCheck(sectionId, recordData.InformationStatus);
                        }
                        
                        // Use both InformationProvided and Observation fields for remarks
                        const remarks = recordData.InformationProvided || recordData.Observation || '';
                        if (remarks) {
                            console.log(`📝 Setting adequacy check remarks: ${remarks}`);
                            handleAdequacyRemarks(sectionId, remarks);
                        }
                    } else {
                        console.log(`ℹ️ No existing DANC record found with NCtype: ${expectedNCType} for section: ${title}`);
                        setExistingDANCRecord(null);
                    }
                } else {
                    setExistingDANCRecord(null);
                }
                setDataLoaded(true);
            } catch (error) {
                console.log('No existing DA NC record found or error loading:', error.message);
                setDancError(`Error loading DA-NC data: ${error.message}`);
                setDataLoaded(true);
            } finally {
                setDancLoading(false);
            }
        };

        loadExistingDANCData();
    }, [orgId, appId, title, sectionId, dataLoaded, handleAdequacyCheck, handleAdequacyRemarks]);

    // Update existingDANCRecord when adequacyChecks change (after refresh)
    useEffect(() => {
        const adequacy = adequacyChecks[sectionId];
        if (adequacy && adequacy.dancRecord) {
            console.log(`🔄 Updating existingDANCRecord from adequacyChecks:`, adequacy.dancRecord);
            setExistingDANCRecord(adequacy.dancRecord);
        }
    }, [adequacyChecks, sectionId]);

    // Load clause numbers for Organization section
    useEffect(() => {
         const loadClauseNumbers = async () => {
               try {
                   console.log('Fetching clause numbers for Lab Staff section...');
       
                   const clauseResponse = await getClauseNo({ standard: '2017', feild: 'Testing' });
       
                   if (clauseResponse.data?.success && clauseResponse.data?.data) {
                       setClauses(clauseResponse.data.data);
                       console.log('Loaded clause numbers:', clauseResponse.data.data.length);
                   } else {
                       console.warn('No clause numbers found or API returned success: false');
                       setClauses([]);
                   }
               } catch (error) {
                   console.error('Error fetching clause numbers:', error);
                   setClauses([]);
               }
           };

        loadClauseNumbers();
    }, [title, existingDANCRecord]);

    // Clear messages after timeout
    useEffect(() => {
        if (dancError || dancSuccess) {
            const timer = setTimeout(() => {
                setDancError('');
                setDancSuccess('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [dancError, dancSuccess]);

    const adequacy = adequacyChecks[sectionId] || { status: '', remarks: '' };

    // Fetch scrutiny remarks when inadequate status is selected
    useEffect(() => {
        const fetchScrutinyRemarks = async () => {
            if (adequacy.status === 'Inadequate') {
                const type = localStorage.getItem('userTypeId') === '6' ? 'Assessment' : 'Document Review';
                try {
                    setLoadingRemarks(true);
                    // Get only Document Review remarks
                    const documentRemarks = await getScrutinyRemarks({ sectionName: title, type });
                    
                    // Set only Document Review remarks
                    if (documentRemarks.data.success) {
                        const remarks = (documentRemarks.data.data || []).map(r => ({...r, type}));
                        setScrutinyRemarks(remarks);
                    } else {
                        setScrutinyRemarks([]);
                    }
                } catch (error) {
                    console.error('Error fetching scrutiny remarks:', error);
                    setScrutinyRemarks([]);
                } finally {
                    setLoadingRemarks(false);
                }
            } else {
                setScrutinyRemarks([]);
            }
        };

        fetchScrutinyRemarks();
    }, [adequacy.status, title]);

    // Don't render if not in adequacy check stage or user type
    // Stage 18: Only show for userTypeId 4 and 6 (editable)
    // Stage 18b: Only show for userTypeId 4 and 6 (editable) - works same as stage 18
    // Stage 18a: Show for any user type (read-only)
    // Stage 20: Only show for userTypeId 4 and 6 (editable) - works same as stage 18
    const userTypeId = localStorage.getItem('userTypeId');
    if ((applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") && userTypeId !== '4' && userTypeId !== '6') return null;
    if (applicationStage !== "18" && applicationStage !== "18a" && applicationStage !== "18b" && applicationStage !== "20") return null;

    // Check if adequacy checks should be read-only (stage 18a)
    const isAdequacyReadOnly = applicationStage === "18a";

    // Standard status change handler (no DA NC calls)
    const handleStatusChange = (status) => {
        if (!isAdequacyReadOnly) {
            handleAdequacyCheck(sectionId, status);
        }
    };

    // Standard remarks change handler with dropdown tracking
    const handleRemarksChange = (remarks, fromDropdown = false) => {
        if (!isAdequacyReadOnly) {
            handleAdequacyRemarks(sectionId, remarks);
            setIsDropdownSelection(fromDropdown);
        }
    };

    // Handle checkbox selection for predefined remarks
    const handleCheckboxChange = (option, checked) => {
        let newSelectedOptions;
        if (checked) {
            newSelectedOptions = [...selectedRemarksOptions, option];
        } else {
            newSelectedOptions = selectedRemarksOptions.filter(item => 
                item.inadequate_remarks !== option.inadequate_remarks
            );
        }
        setSelectedRemarksOptions(newSelectedOptions);
        
        // Update remarks textbox with comma-separated values
        const remarksText = newSelectedOptions
            .map(item => typeof item === 'string' ? item : item.inadequate_remarks)
            .join(', ');
        handleRemarksChange(remarksText, true);
    };

    // Clear all selected checkboxes
    const handleClearAllCheckboxes = () => {
        setSelectedRemarksOptions([]);
        handleRemarksChange('', false);
    };

    // Enhanced submit handler with DA NC API call
    const handleSubmit = async () => {
        try {
            setDancLoading(true);
            setDancError('');
            setDancSuccess('');

            // First submit the adequacy check
            await handleAdequacySubmit(sectionId);

            // Then create/update DA NC record based on status
            if (adequacy.status) {
                if (existingDANCRecord) {
                    // Update existing record
                    try {
                        // Determine NCtype based on userTypeId
                        const userTypeId = localStorage.getItem('userTypeId');
                        let ncType = 'Document Adequacy'; // Default
                        
                        if (userTypeId === '4') {
                            ncType = 'Document Adequacy';
                        } else if (userTypeId === '6') {
                            ncType = 'Assessment';
                        }
                        
                        console.log(`💾 Updating DANC record for userType ${userTypeId} with NCtype: ${ncType}`);

                        // Get assessment parameters from URL for Assessment users
                        const assessmentId = getAssessmentIdFromQuery();
                        const assessorId = getAssessorIdFromQuery();

                        const updateData = {
                            subsectionName: sectionId,
                            informationStatus: adequacy.status,
                            observation: adequacy.status === 'Inadequate'
                                ? (adequacy.remarks || 'Document adequacy marked as Inadequate')
                                : 'Document adequacy check completed',
                            NCtype: ncType,
                            ClauseNo: userTypeId === '6' ? selectedClause : null,
                            isactive: 1
                        };

                        console.log('Updating Record with data:', updateData);
                        console.log('Assessment parameters - assessmentId:', assessmentId, 'assessorId:', assessorId);
                        
                        // Use updateDANCBySourceId to update specific record by sourceid
                        const recordId = existingDANCRecord.sourceid || existingDANCRecord.Id;
                        console.log(`💾 Updating DANC record with sourceid: ${recordId}`);
                        
                        const response = await updateDANCBySourceId(
                            orgId, 
                            appId, 
                            'Organization Details', 
                            recordId, 
                            assessmentId || 0, 
                            assessorId || 0, 
                            updateData
                        );
                        console.log('DA-NC update response:', response);

                        if (response.success) {
                            setExistingDANCRecord(response.data); // Update local state with new data
                            setDancSuccess('Record updated successfully');
                            
                            // Refresh DA-NC data to get updated records
                            if (typeof refreshDANCData === 'function') {
                                await refreshDANCData();
                            }
                        } else {
                            throw new Error(response.message || 'Update failed');
                        }
                    } catch (updateError) {
                        console.error('DA-NC update failed:', updateError);
                        setDancError('Failed to Update: ' + updateError.message);
                    }
                } else {
                    // Create new record  
                    try {
                        // Determine NCtype based on userTypeId
                        const userTypeId = localStorage.getItem('userTypeId');
                        let ncType = 'Document Adequacy'; // Default
                        
                        if (userTypeId === '4') {
                            ncType = 'Document Adequacy';
                        } else if (userTypeId === '6') {
                            ncType = 'Assessment';
                        }
                        
                        console.log(`✨ Creating new DANC record for userType ${userTypeId} with NCtype: ${ncType}`);
                        
                        // Get assessment parameters from URL for Assessment users
                        const assessmentId = getAssessmentIdFromQuery();
                        const assessorId = getAssessorIdFromQuery();

                        const dancData = {
                            Orgid: parseInt(orgId),
                            Appid: parseInt(appId),
                            SectionName: 'Organization Details',
                            SubsectionName: sectionId,
                            InformationProvided: adequacy.remarks || '',
                            InformationStatus: adequacy.status,
                            Observation: adequacy.status === 'Inadequate'
                                ? (adequacy.remarks || 'Document adequacy marked as Inadequate')
                                : 'Document adequacy check completed',
                            Insertedby: currentUserId,
                            NCtype: ncType,
                            ClauseNo: userTypeId === '6' ? selectedClause : null,
                            assessmentId: userTypeId === '6' ? (assessmentId ? parseInt(assessmentId) : null) : 0,
                            assessorId: userTypeId === '6' ? (assessorId ? parseInt(assessorId) : null) : localStorage.getItem('orgId'),
                        };

                        console.log('Creating new Record with data:', dancData);
                        console.log('Assessment parameters - assessmentId:', assessmentId, 'assessorId:', assessorId);
                        
                        const response = await insertDANC(dancData);
                        console.log('DA-NC insert response:', response);

                        if (response.success) {
                            setExistingDANCRecord(response.data); // Store the created record
                            setDancSuccess('Record created successfully');
                            
                            // Refresh DA-NC data to get updated records
                            if (typeof refreshDANCData === 'function') {
                                await refreshDANCData();
                            }
                        } else {
                            throw new Error(response.message || 'Insert failed');
                        }
                    } catch (insertError) {
                        console.error('DA-NC creation failed:', insertError);
                        setDancError('Failed to create Record: ' + insertError.message);
                    }
                }
            }
        } catch (error) {
            console.error('Error submitting adequacy check:', error);
            setDancError('Error submitting adequacy check: ' + error.message);
        } finally {
            setDancLoading(false);
        }
    };

    return (
        <Box sx={{
            mb: 3,
            p: 3,
            backgroundColor: '#f8f9fa',
            borderRadius: 2,
            border: '2px solid #e9ecef',
            borderLeft: '4px solid #007bff'
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{
                    color: '#495057',
                    fontWeight: 600,
                    fontSize: '1.1rem'
                }}>
                    📋 Adequacy Check - {title}
                </Typography>

                {/* {existingDANCRecord && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                            label={`Record: ${existingDANCRecord.InformationStatus || 'Active'}`}
                            size="small"
                            sx={{
                                bgcolor: existingDANCRecord.InformationStatus === 'Adequate' ? '#28a745' : '#dc3545',
                                color: 'white',
                                fontWeight: 'bold'
                            }}
                        />
                        {existingDANCRecord.ClauseNo && (
                            <Chip
                                label={`Clause: ${existingDANCRecord.ClauseNo}`}
                                size="small"
                                sx={{
                                    bgcolor: '#2196f3',
                                    color: 'white',
                                    fontWeight: 'bold'
                                }}
                            />
                        )}
                    </Box>
                )} */}

                {dancLoading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption">Loading DA NC data...</Typography>
                    </Box>
                )}
            </Box>



            <Grid container spacing={3} alignItems="flex-start">
                <Grid item xs={12} md={4}>
                    <FormControl fullWidth sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            borderRadius: 2,
                            '&:hover': {
                                boxShadow: '0 2px 8px rgba(0,123,255,0.15)',
                            },
                            '&.Mui-focused': {
                                boxShadow: '0 4px 12px rgba(0,123,255,0.25)',
                            }
                        }
                    }}>
                        <InputLabel>Document Status</InputLabel>
                        <Select
                            value={adequacy.status}
                            onChange={(e) => handleStatusChange(e.target.value)}
                            label="Document Status"
                            disabled={isAdequacyReadOnly}
                        >
                            <MenuItem value="">-- Select Status --</MenuItem>
                            <MenuItem value="Adequate">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CheckCircleIcon sx={{ color: '#28a745', fontSize: '1rem' }} />
                                    Adequate
                                </Box>
                            </MenuItem>
                            <MenuItem value="Inadequate">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ErrorIcon sx={{ color: '#dc3545', fontSize: '1rem' }} />
                                    Inadequate
                                </Box>
                            </MenuItem>
                            {userTypeId === '4' && (
                            <MenuItem value="To be Verify">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <VerifyIcon sx={{ color: '#35dcdcff', fontSize: '1rem' }} />
                                    To be Verify
                                </Box>
                            </MenuItem>
                            )}
                        </Select>
                    </FormControl>
                </Grid>

                {/* Clause Number Selection - Show for Assessment users (userType 6) */}
                {(localStorage.getItem('userTypeId') === '6' && adequacy.status === 'Inadequate') && (
                    <Grid item xs={12} md={4}>
                        <Autocomplete
                            fullWidth
                            loading={loadingClauses}
                            options={clauses}
                            value={selectedClause ? clauses.find(clause => clause.DisplayName === selectedClause) || null : null}
                            getOptionLabel={(option) => option ? `${option.DisplayName}` : ''}
                            onChange={(event, newValue) => {
                                setSelectedClause(newValue ? newValue.DisplayName : '');
                                console.log('🎯 Selected clause:', newValue);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Clause Number (Assessment)"
                                    placeholder={loadingClauses ? "Loading clauses..." : "Select clause number"}
                                    variant="outlined"
                                    required
                                    InputProps={{
                                        ...params.InputProps,
                                        style: { backgroundColor: 'white' }
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            '&:hover': {
                                                boxShadow: '0 2px 8px rgba(0,123,255,0.15)',
                                            },
                                            '&.Mui-focused': {
                                                boxShadow: '0 4px 12px rgba(0,123,255,0.25)',
                                            }
                                        }
                                    }}
                                />
                            )}
                            renderOption={(props, option) => (
                                <Box component="li" {...props} sx={{ 
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    padding: '8px 16px !important'
                                }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {option.DisplayName}
                                    </Typography>
                                </Box>
                            )}
                            disabled={isAdequacyReadOnly}
                            PopperComponent={(props) => (
                                <div {...props} style={{ ...props.style, zIndex: 1300 }} />
                            )}
                        />
                        <Typography variant="caption" sx={{ 
                            color: '#6c757d',
                            display: 'block',
                            mt: 1,
                            fontStyle: 'italic'
                        }}>
                            {loadingClauses 
                                ? "Loading available clause numbers..."
                                : clauses.length > 0
                                    ? `${clauses.length} clause(s) available for Organization section. Selection required for Assessment users.`
                                    : "No clause numbers found for Organization section. Contact admin to add clauses."
                            }
                        </Typography>
                    </Grid>
                )}

                {adequacy.status === 'Inadequate' && (
                    <>
                        <Grid item xs={12} md={8}>
                            {/* Simple textarea for remarks */}
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label={
                                    isAdequacyReadOnly
                                        ? "Remarks (View Only)"
                                        : "Remarks (Required)"
                                }
                                placeholder={
                                    isAdequacyReadOnly
                                        ? "View Only (Read-only)"
                                        : "Type your detailed remarks here. Use the dropdown below to select predefined remarks."
                                }
                                value={adequacy.remarks || ''}
                                onChange={isAdequacyReadOnly ? undefined : (e) => handleRemarksChange(e.target.value, false)}
                                required={!isAdequacyReadOnly}
                                variant="outlined"
                                disabled={isAdequacyReadOnly}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: isAdequacyReadOnly ? '#f5f5f5' : 'white',
                                        borderRadius: 2,
                                        '&:hover': {
                                            boxShadow: isAdequacyReadOnly ? 'none' : '0 2px 8px rgba(220,53,69,0.15)',
                                        },
                                        '&.Mui-focused': {
                                            boxShadow: isAdequacyReadOnly ? 'none' : '0 4px 12px rgba(220,53,69,0.25)',
                                        }
                                    }
                                }}
                            />
                            
                            {/* Predefined Remarks Dropdown */}
                            {!isAdequacyReadOnly && scrutinyRemarks.length > 0 && (
                                <Box sx={{ mt: 2, position: 'relative' }}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => setIsCheckboxDropdownOpen(!isCheckboxDropdownOpen)}
                                        sx={{ 
                                            mb: 1,
                                            borderColor: '#007bff',
                                            color: '#007bff',
                                            '&:hover': { borderColor: '#0056b3', backgroundColor: '#f8f9ff' }
                                        }}
                                    >
                                        {isCheckboxDropdownOpen ? 'Hide' : 'Show'} Predefined Remarks ({scrutinyRemarks.length} available)
                                    </Button>
                                    
                                    {selectedRemarksOptions.length > 0 && (
                                        <Button
                                            variant="text"
                                            size="small"
                                            onClick={handleClearAllCheckboxes}
                                            sx={{ ml: 1, color: '#dc3545' }}
                                        >
                                            Clear All ({selectedRemarksOptions.length})
                                        </Button>
                                    )}

                                    {isCheckboxDropdownOpen && (
                                        <Paper
                                            elevation={3}
                                            sx={{
                                                mt: 1,
                                                maxHeight: 300,
                                                overflow: 'auto',
                                                border: '1px solid #e0e0e0',
                                                borderRadius: 2
                                            }}
                                        >
                                            <MenuList dense>
                                                {scrutinyRemarks.map((option, index) => {
                                                    const remarkText = typeof option === 'string' ? option : option.inadequate_remarks;
                                                    const isSelected = selectedRemarksOptions.some(
                                                        item => (typeof item === 'string' ? item : item.inadequate_remarks) === remarkText
                                                    );
                                                    
                                                    return (
                                                        <MenuItem
                                                            key={index}
                                                            onClick={() => handleCheckboxChange(option, !isSelected)}
                                                            sx={{
                                                                py: 1,
                                                                px: 2,
                                                                display: 'flex',
                                                                alignItems: 'flex-start',
                                                                minHeight: 'auto'
                                                            }}
                                                        >
                                                            <Checkbox
                                                                checked={isSelected}
                                                                size="small"
                                                                sx={{ p: 0, mr: 2 }}
                                                            />
                                                            <Box sx={{ flexGrow: 1 }}>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontSize: '0.875rem',
                                                                        lineHeight: 1.4,
                                                                        wordBreak: 'break-word',
                                                                        whiteSpace: 'pre-wrap'
                                                                    }}
                                                                >
                                                                    {remarkText}
                                                                </Typography>
                                                                {typeof option !== 'string' && option.type && (
                                                                    <Chip
                                                                        label={option.type}
                                                                        size="small"
                                                                        color={option.type === 'Assessment' ? 'primary' : 'secondary'}
                                                                        variant="outlined"
                                                                        sx={{ 
                                                                            fontSize: '0.7rem', 
                                                                            height: '18px',
                                                                            mt: 0.5
                                                                        }}
                                                                    />
                                                                )}
                                                            </Box>
                                                        </MenuItem>
                                                    );
                                                })}
                                            </MenuList>
                                        </Paper>
                                    )}
                                </Box>
                            )}
                            
                            <Typography variant="caption" sx={{
                                color: '#6c757d',
                                display: 'block',
                                mt: 1,
                                fontStyle: 'italic'
                            }}>
                                {isAdequacyReadOnly
                                    ? "Adequacy check is in read-only mode for stage 18a."
                                    : loadingRemarks
                                        ? "Loading existing remarks..."
                                        : scrutinyRemarks.length > 0
                                            ? `${scrutinyRemarks.length} predefined remark(s) available for "${title}". You can select multiple options from the dropdown above.`
                                            : "No predefined remarks found for this section. Type your detailed remarks in the text area above."
                                }
                            </Typography>
                        </Grid>
                    </>
                )}
            </Grid>

            {adequacy.status && (
                <Box sx={{ mt: 2 }}>
                    {/* <Alert
                        severity={existingDANCRecord
                            ? (adequacy.status === 'Adequate' ? 'info' : 'warning')
                            : (adequacy.status === 'Adequate' ? 'success' : 'warning')
                        }
                        sx={{
                            borderRadius: 2,
                            '& .MuiAlert-icon': {
                                fontSize: '1.2rem'
                            }
                        }}
                    >
                        {existingDANCRecord
                            ? (adequacy.status === 'Adequate'
                                ? `✓ Record exists with status: ${existingDANCRecord.InformationStatus}. Click Update to modify.`
                                : `⚠ Record exists with status: ${existingDANCRecord.InformationStatus}. Click Update to modify.`)
                            : (adequacy.status === 'Adequate'
                                ? '✓ This section is marked as Adequate. Click Submit to create Record.'
                                : '⚠ Please address the remarks above and submit to create Record.')}
                    </Alert> */}

                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            size="medium"
                            onClick={handleSubmit}
                            disabled={
                                !adequacy.status ||
                                (adequacy.status === 'Inadequate' && (!adequacy.remarks || adequacy.remarks.toString().trim() === '')) ||
                                ((localStorage.getItem('userTypeId') === '6' && adequacy.status === 'Inadequate') && !selectedClause) ||
                                loading ||
                                dancLoading ||
                                isAdequacyReadOnly
                            }
                            sx={{
                                background: existingDANCRecord
                                    ? 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)' // Blue gradient for update
                                    : (adequacy.status === 'Adequate'
                                        ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)'
                                        : 'linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)'),
                                color: 'white',
                                fontWeight: 600,
                                px: 3,
                                py: 1,
                                borderRadius: 2,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                                },
                                '&:disabled': {
                                    background: '#e9ecef',
                                    color: '#6c757d',
                                    transform: 'none',
                                    boxShadow: 'none'
                                }
                            }}
                        >
                            {(loading || dancLoading) ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} sx={{ color: 'white' }} />
                                    {existingDANCRecord ? 'Updating...' : 'Submitting...'}
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {existingDANCRecord ? (
                                        <>
                                            🔄 Update
                                        </>
                                    ) : (
                                        <>
                                            📤 submit
                                        </>
                                    )}
                                </Box>
                            )}
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
});

const OrganizationSection = ({ data, onUpdate, fields = [] }) => {
    const { orgId: urlOrgId } = useParams();
    const location = useLocation();
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Function to get orgId from URL query parameters with decryption
    const getOrgIdFromQuery = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedOrgId = searchParams.get('orgId');
        if (encryptedOrgId) {
            try {
                const decryptedOrgId = decryptText(encryptedOrgId);
                console.log('📍 OrganizationSection - Decrypted orgId from URL query:', decryptedOrgId);
                console.log('📍 OrganizationSection - Original encrypted orgId:', encryptedOrgId);
                return decryptedOrgId;
            } catch (error) {
                console.log('⚠️ OrganizationSection - Failed to decrypt orgId, using original:', encryptedOrgId, error);
                return encryptedOrgId; // Return original if decryption fails
            }
        }
        return null;
    }, [location.search]);

    // Function to get appId from URL query parameters with decryption
    const getAppIdFromQuery = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedAppId = searchParams.get('appId');
        if (encryptedAppId) {
            try {
                const decryptedAppId = decryptAppId(encryptedAppId);
                console.log('📍 OrganizationSection - Decrypted appId from URL query:', decryptedAppId);
                console.log('📍 OrganizationSection - Original encrypted appId:', encryptedAppId);
                return decryptedAppId;
            } catch (error) {
                console.log('⚠️ OrganizationSection - Failed to decrypt appId, using original:', encryptedAppId, error);
                return encryptedAppId; // Return original if decryption fails
            }
        }
        return null;
    }, [location.search]);

    // Function to get orgId with priority: URL query (encrypted) > URL params > no default
    const getOrgId = useCallback(() => {
        // First check URL query parameters (encrypted) - highest priority
        const queryOrgId = getOrgIdFromQuery();
        if (queryOrgId) {
            console.log('📍 Using decrypted orgId from URL query:', queryOrgId);
            return queryOrgId;
        }

        // Then check URL parameters (unencrypted)
        if (urlOrgId) {
            console.log('📍 Using orgId from URL params:', urlOrgId);
            return urlOrgId;
        }

        // No fallback - orgId must be provided via URL
        console.log('⚠️ No orgId found in URL parameters');
        return null;
    }, [getOrgIdFromQuery, urlOrgId]);

    // Function to get appId with priority: URL query (encrypted) only
    const getAppId = useCallback(() => {
        // Only check URL query parameters (encrypted)
        const queryAppId = getAppIdFromQuery();
        if (queryAppId) {
            console.log('📍 Using decrypted appId from URL query:', queryAppId);
            return queryAppId;
        }

        // No fallback - appId must be provided via URL query parameters
        console.log('⚠️ No appId found in URL query parameters');
        return null;
    }, [getAppIdFromQuery]);

    // Function to get assessmentId from URL query parameters
  const getAssessmentIdFromQuery = () => {
         const searchParams = new URLSearchParams(location.search);
         const encryptedAssessmentId = searchParams.get("assessmentId");
         if (encryptedAssessmentId) {
             try {
                 const decryptedAssessmentId = decryptText(encryptedAssessmentId);
                 console.log(
                     "📍 Using decrypted assessmentId from URL query:",
                     decryptedAssessmentId
                 );
                 return decryptedAssessmentId;
             } catch (error) {
                 return encryptedAssessmentId;
             }
         }
         return 0;
     };

     // Function to get assessorId from URL query parameters with decryption
       const getAssessorIdFromQuery = () => {
           const searchParams = new URLSearchParams(location.search);
           const encryptedAssessorId = searchParams.get("assessorId");
           if (encryptedAssessorId) {
               try {
                   const decryptedAssessorId = decryptText(encryptedAssessorId);
                   console.log(
                       "📍 Using decrypted assessorId from URL query:",
                       decryptedAssessorId
                   );
                   return decryptedAssessorId;
               } catch (error) {
                   return encryptedAssessorId;
               }
           }
           return 0;
       };
   

    const [orgId, setOrgId] = useState(() => {
        // Initialize with query parameters first, then URL params
        const searchParams = new URLSearchParams(location.search);
        const encryptedOrgId = searchParams.get('orgId');
        if (encryptedOrgId) {
            try {
                const decryptedOrgId = decryptText(encryptedOrgId);
                console.log('🚀 Initial orgId from encrypted query:', decryptedOrgId);
                return decryptedOrgId;
            } catch (error) {
                console.log('Failed to decrypt initial orgId, using original:', encryptedOrgId);
                return encryptedOrgId;
            }
        }
        // Only fallback to URL params, no localStorage
        return urlOrgId || null;
    });
    
    const [appId, setAppId] = useState(() => {
        // Initialize with query parameters only
        const searchParams = new URLSearchParams(location.search);
        const encryptedAppId = searchParams.get('appId');
        if (encryptedAppId) {
            try {
                const decryptedAppId = decryptAppId(encryptedAppId);
                console.log('🚀 Initial appId from encrypted query:', decryptedAppId);
                return decryptedAppId;
            } catch (error) {
                console.log('Failed to decrypt initial appId, using original:', encryptedAppId);
                return encryptedAppId;
            }
        }
        // No fallback - must be provided via query parameters
        return null;
    });
    const [applicationStage, setApplicationStage] = useState(null); // Application stage from LabApplications table

    // Update orgId state only (no localStorage)
    const updateOrgId = (newOrgId) => {
        console.log('Updating orgId state:', newOrgId);
        setOrgId(newOrgId);
    };

    // Update appId state only (no localStorage)
    const updateAppId = (newAppId) => {
        console.log('Updating appId state:', newAppId);
        setAppId(newAppId);
    };
    // Get application stage from LabApplications table
    const getApplicationStage = useCallback(async () => {
        try {
            if (!appId) return;

            const response = await fetch(`${process.env.REACT_APP_API_URL}/laboratory/application-stage/${appId}`);
            const result = await response.json();

            if (result.success) {
                setApplicationStage(result.stage);
                console.log('Fetched application stage:', result.stage);
            } else {
                console.log('Stage not found, defaulting to stage 1 for demo purposes');
                setApplicationStage(1); // Default to 1 for demo purposes
            }
        } catch (error) {
            console.error('Error fetching application stage:', error);
            console.log('Error occurred, defaulting to stage 1 for demo purposes');
            setApplicationStage(1); // Default to 1 for demo purposes
        }
    }, [appId]);

    useEffect(() => {
        getApplicationStage();
        const currentOrgId = getOrgId();
        if (currentOrgId !== orgId) {
            updateOrgId(currentOrgId);
        }

        const currentAppId = getAppId();
        if (currentAppId !== appId) {
            console.log('AppId from URL/localStorage:', currentAppId);
            updateAppId(currentAppId);
        }
    }, [urlOrgId, location.search, orgId, appId, getApplicationStage, getOrgId, getAppId]);

    const [files, setFiles] = useState({
        managementSystemDocument: data?.managementSystemDocument || null,
        organizationChart: data?.organizationChart || null,
        parentOrganizationChart: data?.parentOrganizationChart || null,
    });


    const [isPartOfOrganization, setIsPartOfOrganization] = useState(false);
    const [loadingLaboratoryDetails, setLoadingLaboratoryDetails] = useState(true);



    // New Adequacy Check states for DA-NC integration
    const [adequacyChecks, setAdequacyChecks] = useState({});

    // Unfreeze records state
    const [unfreezeRecords, setUnfreezeRecords] = useState([]);

    // Current user for DA NC system - get from auth context or session
    const [currentUser] = useState(() => {
        return localStorage.getItem('userName') ||
            localStorage.getItem('currentUser') ||
            'System User';
    });

    // Current user ID for DA NC system - get numeric ID for Insertedby field
    const [currentUserId] = useState(() => {
        return parseInt(localStorage.getItem('userId')) ||
            parseInt(localStorage.getItem('currentUserId')) ||
            0;
    });

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    // Helper function to check if a section is unfrozen based on API data
    const isSectionUnfrozen = (sectionTitle) => {
        if (!unfreezeRecords || unfreezeRecords.length === 0) return false;

        const today = new Date();
        // Reset time to start of day for date-only comparison
        today.setHours(0, 0, 0, 0);

        return unfreezeRecords.some(record => {
            if (record.sectionname !== sectionTitle) return false;

            const expiryDate = new Date(record.expirydate);
            // Reset expiry date time to start of day for date-only comparison
            expiryDate.setHours(0, 0, 0, 0);

            // Return true if expiry date is today or in the future (still valid for editing)
            // Compare dates only, not timestamps
            return expiryDate.getTime() >= today.getTime();

            // Return true if expiry date is today or in the future (still valid for editing)
            // This means the unfreeze is still active and user can edit
            return expiryDate.getTime() >= today.getTime();
        });
    };

    // Helper function to check if field should be read-only based on application stage and user type
    const isFieldReadOnly = () => {
        const userTypeId = localStorage.getItem('userTypeId');

        // Check if organization section is unfrozen first - this overrides ALL other conditions
        if (isSectionUnfrozen('Organization Details')) {
            console.log('Organization Details section is unfrozen, allowing edits');
            return false; // Allow editing if section is unfrozen
        }

        // Stage 18 or 18b with userTypeId 4 and 6: all fields are read-only except adequacy checks
        if ((applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") && (userTypeId === '4' || userTypeId === '6')) {
            return true;
        }

        // Stage 18a with any userTypeId: only inadequate sections are editable, adequacy checks are read-only
        if (applicationStage === "18a") {
            const adequacy = adequacyChecks['organization-details'];
            // If section is marked as Inadequate, it's editable; otherwise read-only
            return adequacy?.status !== 'Inadequate';
        }

        return false; // Default: not read-only
    };

    // Handle adequacy check change with useCallback to prevent re-renders
    const handleAdequacyCheck = useCallback((sectionId, value) => {
        setAdequacyChecks(prev => ({
            ...prev,
            [sectionId]: {
                ...prev[sectionId],
                status: value,
                lastUpdated: new Date().toISOString()
            }
        }));
    }, []);

    // Handle adequacy remarks change with useCallback to prevent re-renders
    const handleAdequacyRemarks = useCallback((sectionId, remarks) => {
        setAdequacyChecks(prev => ({
            ...prev,
            [sectionId]: {
                ...prev[sectionId],
                remarks: remarks,
                lastUpdated: new Date().toISOString()
            }
        }));
    }, []);

    // Handle adequacy check submission
    const handleAdequacySubmit = async (sectionId) => {
        const adequacy = adequacyChecks[sectionId];
        if (!adequacy || !adequacy.status) {
            console.warn('No adequacy status to submit for section:', sectionId);
            return;
        }

        try {
            console.log(`Submitting adequacy check for ${sectionId}:`, adequacy);
            // Mark as submitted locally
            setAdequacyChecks(prev => ({
                ...prev,
                [sectionId]: {
                    ...prev[sectionId],
                    isSubmitted: true,
                    submittedAt: new Date().toISOString()
                }
            }));

            showSnackbar(`Adequacy check for ${sectionId} submitted successfully!`, 'success');
        } catch (error) {
            console.error('Error submitting adequacy check:', error);
            showSnackbar('Failed to submit adequacy check', 'error');

            // Revert submission status on error
            setAdequacyChecks(prev => ({
                ...prev,
                [sectionId]: {
                    ...prev[sectionId],
                    isSubmitted: false,
                    submittedAt: null
                }
            }));
        }
    };

    // Load DA NC data for all sections
    const loadDANCData = useCallback(async () => {
        if (!orgId || !appId) return;

        try {
            // Determine expected NCtype based on userTypeId
            const userTypeId = localStorage.getItem('userTypeId');
            let expectedNCType = 'Document Adequacy'; // Default
            
            if (userTypeId === '4') {
                expectedNCType = 'Document Adequacy';
            } else if (userTypeId === '6') {
                expectedNCType = 'Assessment';
            }
            
            console.log(`� OrganizationSection - Loading DANC data for userType ${userTypeId} with expected NCtype: ${expectedNCType}`);
            
            // Get all DA NC records for this org and app
            const response = await getDANC({
                orgid: orgId,
                appid: appId,
                isactive: 1,
                limit: 600  // Increase limit to get all records
            });

            if (response.data.success && response.data.data && response.data.data.length > 0) {
                console.log(`📋 Found ${response.data.data.length} total records, filtering by NCtype: ${expectedNCType}`);
                
                // Filter records by NCtype before processing
                const filteredRecords = response.data.data.filter(record => 
                    record.NCtype === expectedNCType
                );
                
                console.log(`🎯 Filtered to ${filteredRecords.length} records with NCtype: ${expectedNCType}`);
                
                // Create adequacy checks from filtered DA NC data
                const dancAdequacyChecks = {};

                filteredRecords.forEach(record => {

                    // Handle both SubsectionName and direct mapping from SectionName
                    let sectionKey = record.SubsectionName;

                    // If SubsectionName is not available, try to map from SectionName
                    if (!sectionKey && record.SectionName) {
                        const sectionMappings = {
                            'Organization Details': 'organization-details',
                            'Organization': 'organization-details',
                            'Management System': 'management-system',
                            'Organization Chart': 'organization-chart'
                        };
                        sectionKey = sectionMappings[record.SectionName] || record.SectionName.toLowerCase().replace(/\s+/g, '-');
                    }

                    if (sectionKey) {
                        dancAdequacyChecks[sectionKey] = {
                            status: record.InformationStatus || '',
                            remarks: record.InformationProvided || record.Observation || '',
                            isSubmitted: false, // Allow users to modify and resubmit
                            submittedAt: record.updatedate || record.Insertdate,
                            dancRecord: record, // Store original record for reference
                            sourceid: record.sourceid || record.Id // Store sourceid for updates
                        };
                        
                        console.log(`✅ Stored DANC record for section "${sectionKey}" with sourceid: ${record.sourceid || record.Id}`);

                        console.log(`Mapped Record to section "${sectionKey}":`, {
                            status: record.InformationStatus,
                            remarks: record.InformationProvided || record.Observation
                        });
                    }
                });

                // Merge with existing adequacy checks
                setAdequacyChecks(prev => {
                    const merged = {
                        ...prev,
                        ...dancAdequacyChecks
                    };
                    console.log('Final merged adequacy checks:', merged);
                    return merged;
                });

            } else {
                console.log('No Records found in response');
            }
        } catch (error) {
            console.error('Error loading DA-NC data:', error);
            showSnackbar('Error loading DA-NC data: ' + error.message, 'error');
        }
    }, [orgId, appId]);

    // Load unfreeze records
    const loadUnfreezeRecords = useCallback(async () => {
        if (!appId) return;

        try {
            const response = await UnfreezeApiService.getUnfreezeRecords(appId, orgId);
            if (response.success) {
                console.log('Unfreeze records loaded:', response.data);
                setUnfreezeRecords(response.data);
            }
        } catch (err) {
            console.error("Load unfreeze records error:", err);
        }
    }, [appId, orgId]);

    // Load laboratory details to check if laboratory is part of organization  
    const loadLaboratoryDetails = useCallback(async () => {
        try {
            setLoadingLaboratoryDetails(true);
            console.log('🔄 OrganizationSection - Loading laboratory details with orgId:', orgId);
            const response = await TestingLaboratoryService.getLaboratoryDetails(orgId);

            if (response.success && response.data) {
                // Check if laboratory is part of organization (IsParentOrgApplicable field)
                const isPartOfOrg = response.data.IsParentOrgApplicable === true ||
                    response.data.IsParentOrgApplicable === 'Yes' ||
                    response.data.IsParentOrgApplicable === 1;
                setIsPartOfOrganization(isPartOfOrg);
                console.log('Laboratory is part of organization:', isPartOfOrg);
            } else {
                setIsPartOfOrganization(false);
                console.log('No laboratory details found or failed to load');
            }
        } catch (error) {
            console.error('Error loading laboratory details:', error);
            setIsPartOfOrganization(false);
        } finally {
            setLoadingLaboratoryDetails(false);
        }
    }, [orgId]);


    // Load existing organization documents on component mount
    const loadOrganizationDocuments = useCallback(async () => {
        try {
            console.log('📄 OrganizationSection - Loading organization documents with appId:', appId);
            const response = await TestingLaboratoryService.getOrganizationDocuments(appId);

            if (response.success && response.data) {
                console.log('📄 Raw API response data:', response.data);
                
                // Map PascalCase API keys to camelCase component keys
                const mappedData = {
                    managementSystemDocument: response.data.ManagementSystemDocument,
                    organizationChart: response.data.OrganizationChart,
                    parentOrganizationChart: response.data.ParentOrganizationChart
                };

                console.log('📄 Mapped data:', mappedData);

                const existingFiles = {};

                // Use batch processing utility for cleaner code
                const fileFields = ['managementSystemDocument', 'organizationChart', 'parentOrganizationChart'];
                const processedFiles = await s3Utils.batchProcessForm153Files(mappedData, fileFields);

                console.log('📄 Processed files from s3Utils:', processedFiles);

                // Map the processed files to the expected structure
                Object.assign(existingFiles, processedFiles);

                console.log('📄 Final existingFiles to set:', existingFiles);

                setFiles(prevFiles => ({ ...prevFiles, ...existingFiles }));


            }
        } catch (error) {
            console.error('Error loading organization documents:', error);
            // Don't show error snackbar for missing documents as it's normal for new records
        }
    }, [appId]);

    // Load initial data including DA-NC data
    const loadInitialData = useCallback(async () => {
        if (orgId && appId) { // Only load if we have both orgId and appId from URL parameters
            await loadLaboratoryDetails();
            await loadOrganizationDocuments();
            await loadDANCData();
            await loadUnfreezeRecords();
        } else {
            console.log('⚠️ Missing orgId or appId from URL parameters, skipping data load');
            setLoadingLaboratoryDetails(false);
        }
    }, [orgId, appId, loadLaboratoryDetails, loadOrganizationDocuments, loadDANCData, loadUnfreezeRecords]);

    useEffect(() => {
        loadInitialData();
    }, [loadInitialData]);



    // Check if any files exist to determine button text
    const hasExistingFiles = () => {
        return Object.values(files).some(file => file !== null);
    };

    const handleFileChange = (fieldName, fileData) => {
        console.log(`File change for ${fieldName}:`, fileData);

        const newFiles = {
            ...files,
            [fieldName]: fileData,
        };
        setFiles(newFiles);
        onUpdate && onUpdate(newFiles);

        if (fileData) {
            showSnackbar('File uploaded successfully!');
        } else {
            showSnackbar('File removed successfully!', 'success');
        }
    };

    const handleSubmit = async () => {
        // Check required files based on laboratory configuration
        const requiredFiles = ['managementSystemDocument', 'organizationChart'];
        if (isPartOfOrganization) {
            requiredFiles.push('parentOrganizationChart');
        }

        const missingFiles = requiredFiles.filter(fileField => !files[fileField]);

        if (missingFiles.length > 0) {
            const fileLabels = {
                'managementSystemDocument': 'Management System Document',
                'organizationChart': 'Organization Chart',
                'parentOrganizationChart': 'Parent Organization Chart'
            };

            const missingFileNames = missingFiles.map(field => fileLabels[field]).join(', ');
            showSnackbar(`Please upload the following required document(s): ${missingFileNames}`, 'warning');
            return;
        }

        try {
            // Prepare document data with file names/keys
            // Try multiple possible properties to extract the filename
            const getFileName = (fileData) => {
                if (!fileData) return null;

                console.log('File data structure:', fileData);

                // The backend returns an object with properties like: key, fileName, originalName, etc.
                // Priority: use key first (the S3 key), then fileName, then originalName
                return fileData.key ||
                    fileData.fileName ||
                    fileData.originalName ||
                    fileData.name ||
                    fileData.file?.name ||
                    (typeof fileData === 'string' ? fileData : null);
            };

            const documentData = {
                managementSystemDocument: getFileName(files.managementSystemDocument),
                organizationChart: getFileName(files.organizationChart),
                parentOrganizationChart: getFileName(files.parentOrganizationChart)
            };

            console.log('Document data to send:', documentData);

            console.log('💾 OrganizationSection - Updating organization documents with appId:', appId, 'documentData:', documentData);
            
            // Call API to update organization documents
            const response = await TestingLaboratoryService.updateOrganizationDocuments(appId, documentData);

            const isUpdate = hasExistingFiles();

            if (response.success) {
                showSnackbar(
                    isUpdate
                        ? 'Organization documents updated successfully!'
                        : 'Organization documents submitted successfully!',
                    'success'
                );
                console.log('API Response:', response);
            } else {
                showSnackbar(
                    response.message ||
                    (isUpdate
                        ? 'Failed to update organization documents'
                        : 'Failed to submit organization documents'),
                    'error'
                );
            }
        } catch (error) {
            console.error('Error submitting organization documents:', error);
            const isUpdate = hasExistingFiles();
            showSnackbar(
                error.message ||
                (isUpdate
                    ? 'Failed to update organization documents'
                    : 'Failed to submit organization documents'),
                'error'
            );
        }
    };

    return (
        <Box sx={{
            borderTopRightRadius: 8,
            borderTopLeftRadius: 8,
            maxWidth: '100vw',
            minHeight: '70vh',
        }}>
            {/* Header */}
            <Box sx={{
                py: 2,
                px: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                '& .MuiAccordionSummary-content': {
                    margin: '16px 0',
                },
                '& .MuiSvgIcon-root': {
                    color: 'white',
                }
            }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    🏢 Organization Details
                </Typography>

                {/* Section Status Indicators */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {/* Unfrozen section indicator */}
                    {isSectionUnfrozen('Organization Details') && (
                        <Chip
                            label="🔓 UNFROZEN"
                            size="small"
                            sx={{
                                bgcolor: '#ff9800',
                                color: 'white',
                                fontWeight: 'bold',
                                animation: 'pulse 2s infinite'
                            }}
                        />
                    )}

                    {/* Stage 18 or 18b or 20 Read-only indicator */}
                    {(applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") && (localStorage.getItem('userTypeId') === '4' || localStorage.getItem('userTypeId') === '6') && (
                        <Chip
                            label="📖 Read Only Mode"
                            size="small"
                            sx={{
                                bgcolor: '#2196f3',
                                color: 'white',
                                fontWeight: 'bold'
                            }}
                        />
                    )}

                    {/* Stage 18a indicators */}
                    {applicationStage === "18a" && (
                        <>
                            {adequacyChecks['organization-details']?.status && (
                                <Chip
                                    label={`Organization Details: ${adequacyChecks['organization-details'].status}`}
                                    size="small"
                                    sx={{
                                        bgcolor: adequacyChecks['organization-details'].status === 'Adequate' ? '#4caf50' : '#f44336',
                                        color: 'white',
                                        fontWeight: 'bold'
                                    }}
                                />
                            )}
                            <Chip
                                label="🔒 Adequacy Checks Read-Only"
                                size="small"
                                sx={{
                                    bgcolor: '#9e9e9e',
                                    color: 'white',
                                    fontWeight: 'bold'
                                }}
                            />
                        </>
                    )}
                </Box>

            </Box>

            {/* Unfrozen Sections Notification */}
            {isSectionUnfrozen('Organization Details') && (
                <Alert severity="warning" sx={{ m: 2, borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        🔓 Sections Unfrozen for Editing
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        The Organization Details section has been temporarily unfrozen for editing. You can now modify the fields and documents.
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Chip
                            label="Organization Details"
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ fontWeight: 'bold' }}
                        />
                    </Box>
                </Alert>
            )}

            {/* File Upload Grid */}
            {loadingLaboratoryDetails ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                    <CircularProgress size={40} />
                    <Typography variant="body1" sx={{ ml: 2 }}>
                        Loading laboratory details...
                    </Typography>
                </Box>
            ) : (
                <Grid container spacing={3} sx={{ p: 2 }}>

                    {/* DA-NC Integrated Adequacy Check Section */}
                    {(((applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") && (localStorage.getItem('userTypeId') === '4' || localStorage.getItem('userTypeId') === '6')) || applicationStage === "18a") && (
                        <Grid item xs={12}>
                            <AdequacyCheckSection
                                sectionId="organization-details"
                                title="Organization Details"
                                applicationStage={applicationStage}
                                orgId={orgId}
                                appId={appId}
                                adequacyChecks={adequacyChecks}
                                handleAdequacyCheck={handleAdequacyCheck}
                                handleAdequacyRemarks={handleAdequacyRemarks}
                                handleAdequacySubmit={handleAdequacySubmit}
                                loading={false}
                                currentUser={currentUser}
                                currentUserId={currentUserId}
                                getAssessmentIdFromQuery={getAssessmentIdFromQuery}
                                getAssessorIdFromQuery={getAssessorIdFromQuery}
                                refreshDANCData={loadDANCData}
                            />
                        </Grid>
                    )}
                    {/* Management System Document */}
                    <Grid item xs={12} md={4}>
                        <Card
                            elevation={2}
                            sx={{
                                height: '100%',
                                borderRadius: 2,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: 3,
                                },
                            }}
                        >
                            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                                        Management System Document
                                        <span style={{ color: 'red', fontWeight: 'bold', marginLeft: '4px' }}>*</span>
                                    </Typography>
                                    {isFieldReadOnly() && (
                                        <Chip
                                            label={(applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") ? "📖 Read Only" : "🔒 Locked"}
                                            size="small"
                                            sx={{
                                                bgcolor: (applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") ? '#2196f3' : '#9e9e9e',
                                                color: 'white',
                                                fontWeight: 'bold'
                                            }}
                                        />
                                    )}
                                    {applicationStage === "18a" && adequacyChecks['organization-details']?.status === 'Inadequate' && (
                                        <Chip
                                            label="✏️ Editable"
                                            size="small"
                                            sx={{
                                                bgcolor: '#4caf50',
                                                color: 'white',
                                                fontWeight: 'bold'
                                            }}
                                        />
                                    )}
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <FileUploadComponent
                                        fieldName="managementSystemDocument"
                                        label="Management System Document"
                                        orgId={orgId}
                                        section="organization"
                                        required={true}
                                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                        maxSize={250}
                                        multiple={false}
                                        onChange={(fieldName, fileData) => handleFileChange(fieldName, fileData)}
                                        initialFile={files.managementSystemDocument}
                                        disabled={isFieldReadOnly()}
                                        readOnly={isFieldReadOnly()}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>



                    {/* Organization Chart */}
                    <Grid item xs={12} md={4}>
                        <Card
                            elevation={2}
                            sx={{
                                height: '100%',
                                borderRadius: 2,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: 3,
                                },
                            }}
                        >
                            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                                        Organization Chart
                                        <span style={{ color: 'red', fontWeight: 'bold', marginLeft: '4px' }}>*</span>
                                    </Typography>
                                    {isFieldReadOnly() && (
                                        <Chip
                                            label={(applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") ? "📖 Read Only" : "🔒 Locked"}
                                            size="small"
                                            sx={{
                                                bgcolor: (applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") ? '#2196f3' : '#9e9e9e',
                                                color: 'white',
                                                fontWeight: 'bold'
                                            }}
                                        />
                                    )}
                                    {applicationStage === "18a" && adequacyChecks['organization-details']?.status === 'Inadequate' && (
                                        <Chip
                                            label="✏️ Editable"
                                            size="small"
                                            sx={{
                                                bgcolor: '#4caf50',
                                                color: 'white',
                                                fontWeight: 'bold'
                                            }}
                                        />
                                    )}
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <FileUploadComponent
                                        fieldName="organizationChart"
                                        label="Organization Chart"
                                        orgId={orgId}
                                        section="organization"
                                        required={true}
                                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                        maxSize={10}
                                        multiple={false}
                                        onChange={(fieldName, fileData) => handleFileChange(fieldName, fileData)}
                                        initialFile={files.organizationChart}
                                        disabled={isFieldReadOnly()}
                                        readOnly={isFieldReadOnly()}
                                    />

                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Parent Organization Chart - Only show if laboratory is part of organization */}
                    {isPartOfOrganization && (
                        <Grid item xs={12} md={4}>
                            <Card
                                elevation={2}
                                sx={{
                                    height: '100%',
                                    borderRadius: 2,
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: 3,
                                    },
                                }}
                            >
                                <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                                            Parent Organization Chart
                                            <span style={{ color: 'red', fontWeight: 'bold', marginLeft: '4px' }}>*</span>
                                        </Typography>
                                        {isFieldReadOnly() && (
                                            <Chip
                                                label={(applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") ? "📖 Read Only" : "🔒 Locked"}
                                                size="small"
                                                sx={{
                                                    bgcolor: (applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") ? '#2196f3' : '#9e9e9e',
                                                    color: 'white',
                                                    fontWeight: 'bold'
                                                }}
                                            />
                                        )}
                                        {applicationStage === "18a" && adequacyChecks['organization-details']?.status === 'Inadequate' && (
                                            <Chip
                                                label="✏️ Editable"
                                                size="small"
                                                sx={{
                                                    bgcolor: '#4caf50',
                                                    color: 'white',
                                                    fontWeight: 'bold'
                                                }}
                                            />
                                        )}
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <FileUploadComponent
                                            fieldName="parentOrganizationChart"
                                            label="Parent Organization Chart"
                                            orgId={orgId}
                                            section="organization"
                                            required={true}
                                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                            maxSize={10}
                                            multiple={false}
                                            onChange={(fieldName, fileData) => handleFileChange(fieldName, fileData)}
                                            initialFile={files.parentOrganizationChart}
                                            disabled={isFieldReadOnly()}
                                            readOnly={isFieldReadOnly()}
                                        />

                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {/* Information message when Parent Organization Chart is not applicable */}
                    {!loadingLaboratoryDetails && !isPartOfOrganization && (
                        <Grid item xs={12}>
                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    <strong>Parent Organization Chart:</strong> Not applicable - This laboratory is not part of a larger organization.
                                    <br />
                                    <small>If this is incorrect, please ensure the "Is Laboratory part of an Organization" field is set to "Yes" in the Laboratory Section.</small>
                                </Typography>
                            </Alert>
                        </Grid>
                    )}



                    {/* Submit/Update Button */}
                    <Grid item xs={12} sx={{ textAlign: 'right', mt: 2 }}>
                        {hasExistingFiles() && (
                            <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
                                <Typography variant="body2">
                                    📄 <strong>Documents Status:</strong> {Object.values(files).filter(file => file !== null).length} document(s) uploaded.
                                    Click "Update" to modify existing documents.
                                </Typography>
                            </Alert>
                        )}
                        {((applicationStage === null || applicationStage === "" || applicationStage === undefined)) && (
                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleSubmit}
                                sx={{
                                    ml: 'auto',
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    background: hasExistingFiles()
                                        ? 'linear-gradient(135deg, #134d29cb, #134d29cb)'
                                        : 'linear-gradient(135deg, #667eea, #764ba2)',
                                    '&:hover': {
                                        background: hasExistingFiles()
                                            ? 'linear-gradient(135deg, #134d29cb, #134d29cb)'
                                            : 'linear-gradient(135deg, #5a6fd8, #6a42a0)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: hasExistingFiles()
                                            ? '0 8px 25px rgba(255, 152, 0, 0.4)'
                                            : '0 8px 25px rgba(102, 126, 234, 0.4)',
                                    },
                                }}
                            >
                                {hasExistingFiles() ? 'Update Organization Documents' : 'Submit Organization Documents'}
                            </Button>
                        )}

                        {/* Show update button for stage 18a only if organization-details section is inadequate OR if section is unfrozen */}
                        {(
    (applicationStage === "18a" && adequacyChecks['organization-details']?.status === 'Inadequate') ||    isSectionUnfrozen('Organization Details')) && (
                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleSubmit}
                                sx={{
                                    ml: 'auto',
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    background: 'linear-gradient(135deg, #ff9800, #f57c00)',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #f57c00, #ef6c00)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 8px 25px rgba(255, 152, 0, 0.4)',
                                    },
                                }}
                            >
                                Update Organization Documents
                            </Button>
                        )}
                    </Grid>


                    {/* Organization Chart Requirements Note */}
                    <Grid item xs={12} sx={{ mt: 1 }}>
                        <Box sx={{ p: 3, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e9ecef' }}>
                            <Typography variant="subtitle1" sx={{ mb: 2, color: '#2c3e50', fontWeight: 600 }}>
                                📋 Organization Chart Requirements:
                            </Typography>

                            <Box sx={{ pl: 2, borderLeft: '3px solid #667eea' }}>
                                <Typography variant="body2" sx={{ mb: 2, color: '#555', lineHeight: 1.6 }}>
                                    <strong>i.</strong> Indicate in an organization chart the operating departments of the Testing laboratory for which accreditation is being sought (Append).
                                </Typography>

                                <Typography variant="body2" sx={{ mb: 2, color: '#555', lineHeight: 1.6 }}>
                                    <strong>ii.</strong> Indicate how the applicant laboratory is related to parent organization, where applicable. Please also indicate if some of the responsibilities as described in ISO 17025 are shared by the parent organization.
                                    {isPartOfOrganization && (
                                        <span style={{ color: '#d32f2f', fontWeight: 'bold' }}> (Required for your laboratory as it is part of an organization)</span>
                                    )}
                                </Typography>

                                <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff3cd', borderRadius: 1, border: '1px solid #ffeaa7' }}>
                                    <Typography variant="body2" sx={{ color: '#856404', fontWeight: 500, mb: 1 }}>
                                        📌 Note:
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#856404', lineHeight: 1.5, mb: 2 }}>
                                        <strong>If the laboratory is a defined part of a legal entity,</strong> then the organization structure of the Legal entity, with place of the laboratory within the larger structure (legal entity/parent organization) shall also be submitted, clearly identifying the other activities performed by the legal entity.
                                        {isPartOfOrganization && (
                                            <span style={{ color: '#d32f2f', fontWeight: 'bold' }}> (This applies to your laboratory)</span>
                                        )}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#856404', lineHeight: 1.5 }}>
                                        <strong>If the laboratory is one of the laboratories in a chain of laboratories,</strong> then the name and address of the main laboratory (however named, if applicable) and the relationship between the two shall also be described.
                                    </Typography>
                                    {!isPartOfOrganization && (
                                        <Typography variant="body2" sx={{ color: '#2e7d32', lineHeight: 1.5, mt: 2, fontWeight: 500 }}>
                                            ✅ <strong>Your laboratory is not part of an organization,</strong> so the Parent Organization Chart is not required.
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            )}


            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ borderRadius: 2 }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default OrganizationSection;
